import { FC, useRef, useState, useCallback } from 'react';
import {
	Box,
	Flex,
	Text,
	Portal,
} from 'src/components/ui';
import TemplatePreview, { TemplatePreviewHandle } from 'src/components/templates/TemplatePreview';
import { FaPlay, FaPause, FaTimes } from 'react-icons/fa';
import useSmoothProgress from './useSmoothProgress';

interface VideoExpandModalProps {
	isOpen: boolean;
	onClose: () => void;
	template: any;
	creativeId: string;
}

const VideoExpandModal: FC<VideoExpandModalProps> = ({ isOpen, onClose, template, creativeId }) => {
	const previewRef = useRef<TemplatePreviewHandle>(null);
	const [previewReady, setPreviewReady] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(10);
	const seekBarRef = useRef<HTMLDivElement>(null);
	const { progressBarRef, timeDisplayRef, onTimeChange, setTimeImmediate } = useSmoothProgress({ duration, isPlaying });

	const onPreviewReady = useCallback(() => {
		setPreviewReady(true);
		setTimeout(() => {
			if (previewRef.current) {
				previewRef.current.setLoop(false);
				previewRef.current.setControls(false);
				previewRef.current.setTime(0);
				previewRef.current.play();
				setDuration(previewRef.current.getDuration() || 10);
			}
		}, 300);
	}, []);

	const handlePlayPause = useCallback(() => {
		if (!previewRef.current) return;
		if (isPlaying) {
			previewRef.current.pause();
		} else {
			previewRef.current.play();
		}
	}, [isPlaying]);

	// Auto-correct duration if the SDK reports a larger playback time than the cached value
	// (happens when getDuration() returns a partial value right after onReady).
	const handleTimeChange = useCallback((time: number) => {
		if (time > duration + 0.1) {
			setDuration(time);
		}
		onTimeChange(time);
	}, [duration, onTimeChange]);

	const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (!seekBarRef.current || !previewRef.current) return;
		const rect = seekBarRef.current.getBoundingClientRect();
		const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
		const newTime = ratio * duration;
		previewRef.current.setTime(newTime);
		setTimeImmediate(newTime);
	}, [duration, setTimeImmediate]);

	const onPlay = useCallback(() => setIsPlaying(true), []);
	const onPause = useCallback(() => setIsPlaying(false), []);

	if (!isOpen) return null;

	return (
		<Portal>
			{/* Full-screen overlay */}
			<Flex
				position="fixed"
				top={0}
				left={0}
				right={0}
				bottom={0}
				zIndex={10500}
				bg="blackAlpha.900"
				align="center"
				justify="center"
				onClick={onClose}
			>
				{/* Close button */}
				<Flex
					position="absolute"
					top={4}
					right={4}
					w="40px"
					h="40px"
					borderRadius="full"
					bg="whiteAlpha.200"
					align="center"
					justify="center"
					cursor="pointer"
					_hover={{ bg: 'whiteAlpha.400' }}
					onClick={onClose}
					zIndex={10501}
				>
					<Box as={FaTimes} color="white" fontSize="18px" />
				</Flex>

				{/* Video + controls column */}
				<Flex
					direction="column"
					align="center"
					maxH="90vh"
					maxW={{ base: '95vw', md: '85vw', lg: '75vw' }}
					onClick={(e: React.MouseEvent) => e.stopPropagation()}
				>
					{/* Video */}
					<Box
						w={{ base: '90vw', md: '75vh', lg: '80vh' }}
						h={{ base: '90vw', md: '75vh', lg: '80vh' }}
						maxW={{ base: '95vw', md: '85vw', lg: '75vw' }}
						maxH="75vh"
						borderTopRadius="lg"
						overflow="hidden"
					>
						<TemplatePreview
							ref={previewRef}
							key={`expand-${creativeId}`}
							template={template}
							onReady={onPreviewReady}
							onTimeChange={handleTimeChange}
							onPlay={onPlay}
							onPause={onPause}
						/>
					</Box>

					{/* Controls bar - outside video */}
					<Flex
						w={{ base: '90vw', md: '75vh', lg: '80vh' }}
						maxW={{ base: '95vw', md: '85vw', lg: '75vw' }}
						bg="gray.900"
						px={4}
						py={3}
						align="center"
						gap={3}
						borderBottomRadius="lg"
					>
						<Box
							as="button"
							onClick={handlePlayPause}
							color="white"
							cursor="pointer"
							flexShrink={0}
							_hover={{ opacity: 0.8 }}
						>
							<Box as={isPlaying ? FaPause : FaPlay} fontSize="16px" ml={isPlaying ? '0' : '2px'} />
						</Box>
						<Box
							ref={seekBarRef}
							flex={1}
							h="8px"
							bg="whiteAlpha.300"
							borderRadius="full"
							cursor="pointer"
							onClick={handleSeek}
							position="relative"
						>
							<Box
								ref={progressBarRef}
								h="100%"
								bg="white"
								borderRadius="full"
								w="0%"
							/>
						</Box>
						<Text ref={timeDisplayRef} color="white" fontSize="sm" flexShrink={0} whiteSpace="nowrap">
							0:00 / 0:00
						</Text>
					</Flex>
				</Flex>
			</Flex>
		</Portal>
	);
};

export default VideoExpandModal;
